import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { SearchResult } from '../lib/types';
import { stripFrontmatter } from '../lib/formatters';
import { CopyButtons } from './CopyButtons';

interface ResultCardProps {
  result: SearchResult;
  active: boolean;
  hovered: boolean;
  onSelect: () => void;
  onHover: (hovering: boolean) => void;
}

const PREVIEW_CHARS = 420;

export function ResultCard({ result, active, hovered, onSelect, onHover }: ResultCardProps) {
  const [expanded, setExpanded] = useState(false);
  const fullHtmlRef = useRef<HTMLDivElement>(null);

  const fullBody = stripFrontmatter(result.body_md ?? '');
  const isLong = fullBody.length > PREVIEW_CHARS;
  const shown = expanded || !isLong ? fullBody : fullBody.slice(0, PREVIEW_CHARS) + '…';

  const className = ['card', active ? 'active' : '', hovered ? 'hover' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <article
      className={className}
      data-slug={result.slug}
      onClick={onSelect}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <header className="card-header">
        <h2 className="card-title">{result.title}</h2>
        <span className="tier-badge" data-tier={result.tier}>
          {result.tier}
        </span>
      </header>
      <div className="card-meta">
        <span>{result.page_type}</span>
        <span>score {result.score.toFixed(3)}</span>
        <span>{result.slug}</span>
        {result.traversed_from && (
          <span className="traversed">← {result.traversed_from}</span>
        )}
      </div>
      <div className="card-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{shown}</ReactMarkdown>
      </div>
      <div ref={fullHtmlRef} className="card-hidden" aria-hidden>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{fullBody}</ReactMarkdown>
      </div>
      <CopyButtons result={result} htmlRef={fullHtmlRef} />
      {isLong && (
        <button
          type="button"
          className="card-expand"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? '— collapse' : '+ expand'}
        </button>
      )}
    </article>
  );
}
